import React from 'react';
import I18n from 'i18n-js';
import gql from 'graphql-tag';
import { ChildProps } from 'react-apollo';

import { RCRadioCardGroup, RCToggledContent } from '@reverbdotcom/cadence/components';

import { parseAmountCents } from '../money';
import SanitizedRender from '../components/sanitized_render';
import { TERMS_OF_USE_PATH, ACCOUNT_SETTINGS_PATH } from '../url_helpers';
import {
  core_apimessages_Money,
  core_apimessages_Address,
  core_apimessages_CreditCard,
  CommonsOffersImmediatePaymentReview,
} from '../rql';
import { IUser } from '../components/user_context_provider';
import { connect } from '../connect';
import { withGraphql } from '../with_graphql';

interface IExternalProps {
  offerPrice: core_apimessages_Money;
  shippingPrice: core_apimessages_Money;
  currency: string;
  selectedAddress: core_apimessages_Address;
  allAddresses: core_apimessages_Address[];
  selectedCreditCard: core_apimessages_CreditCard;
  allCreditCards: core_apimessages_CreditCard[];
  dispatch: (any) => void;
  user: IUser;
  localPickupOnly?: boolean;
}

export type IProps = ChildProps<IExternalProps, CommonsOffersImmediatePaymentReview.Query>;

function displayTextForAddress(address) {
  return `${address.name} - ${address.streetAddress} ${address.displayLocation}`;
}

export function ImmediatePaymentReview({ offerPrice, shippingPrice, currency, selectedAddress, allAddresses, selectedCreditCard, allCreditCards, dispatch, user, data, localPickupOnly = false }: IProps) {
  const { loading } = data || {};

  const taxEstimate = !loading && data.estimateNegotiationSalesTax?.amountTax;

  if (loading) {
    return (
      <div className="offers-form offers-form--loading">
        <div className="offers-form-placeholder" />
        <div className="offers-form-placeholder" />
        <div className="offers-form-placeholder" />
      </div>
    );
  }

  return (
    <div className="offers-form__payment-review">
      <div className="offers-form__payment-review__totals">
        <div className="d-flex fx-justify-between mb-1">
          <span>{I18n.t('commons.offers.immediatePayment.totals.yourOfferPrice')}</span>
          {offerPrice.display}
        </div>

        <div className="d-flex fx-justify-between mb-1">
          <span>{I18n.t('commons.offers.immediatePayment.totals.shipping')}</span>
          {shippingPrice.display}
        </div>

        {taxEstimate?.display && (
          <div className="d-flex fx-justify-between mb-1">
            <span>{I18n.t('commons.offers.immediatePayment.totals.tax')}</span>
            {taxEstimate.display}
          </div>
        )}

        <div className="offers-form__payment-review__totals__estimated-total">
          <div>
            <h4>
              {taxEstimate?.display && I18n.t('commons.offers.immediatePayment.totals.total')}
              {!taxEstimate?.display && I18n.t('commons.offers.immediatePayment.totals.totalWithoutTax')}
            </h4>
            <div>{I18n.t('commons.offers.immediatePayment.totals.ifAccepted')}</div>

            {!taxEstimate?.display && I18n.t('commons.offers.immediatePayment.totals.taxNote')}
          </div>

          {parseAmountCents(offerPrice.amountCents + shippingPrice.amountCents + (taxEstimate?.amountCents || 0), currency).display}
        </div>
      </div>

      <RCToggledContent
        id="shipTo"
        heading={I18n.t('commons.offers.immediatePayment.shipTo.label')}
        additionalHeadingContent={displayTextForAddress(selectedAddress)}
      >
        {allAddresses.length > 1 && (
          <RCRadioCardGroup
            id="shipToGroup"
            name="shipToGroup"
            label={I18n.t('commons.offers.immediatePayment.shipTo.label')}
            value={selectedAddress.id}
            onChange={(e) => { dispatch({ type: 'setShipTo', value: allAddresses.find((addr) => addr.id === e.target.value) }); }}
            required
          >
            {allAddresses.map((address) => {
              return (
                <RCRadioCardGroup.Option
                  key={address.id}
                  label={displayTextForAddress(address)}
                  value={address.id}
                />
              );
            })}
          </RCRadioCardGroup>
        )}
        <p className="offers-form__payment-review__additional-content">
          <SanitizedRender
            html={I18n.t('commons.offers.immediatePayment.shipTo.helpText', {
              accountLinkOpen: `<a href='${ACCOUNT_SETTINGS_PATH}'>`,
              accountLinkClose: '</a>',
            })}
          />
        </p>
      </RCToggledContent>

      <RCToggledContent
        id="payWith"
        heading={I18n.t('commons.offers.immediatePayment.payWith.label')}
        additionalHeadingContent={selectedCreditCard.display.summary}
      >
        {allCreditCards.length > 1 && (
          <RCRadioCardGroup
            id="payWithGroup"
            name="payWithGroup"
            label={I18n.t('commons.offers.immediatePayment.payWith.label')}
            value={selectedCreditCard.id}
            onChange={(e) => { dispatch({ type: 'setPaymentMethod', value: allCreditCards.find((cc) => cc.id === e.target.value) }); }}
            required
          >
            {allCreditCards.map((card) => {
              return (
                <RCRadioCardGroup.Option
                  key={card.id}
                  label={card.display.summary}
                  value={card.id}
                />
              );
            })}
          </RCRadioCardGroup>
        )}
        <p className="offers-form__payment-review__additional-content">
          {I18n.t('commons.offers.immediatePayment.payWith.helpText')}
        </p>
      </RCToggledContent>

      <div className="offers-form__payment-review__non-toggled-content">
        <span>{I18n.t('commons.offers.immediatePayment.delivery.label')}</span>
        {!localPickupOnly && I18n.t('commons.offers.immediatePayment.delivery.standard')}
        {localPickupOnly && I18n.t('commons.offers.immediatePayment.delivery.localPickup')}
      </div>

      <div className="offers-form__payment-review__non-toggled-content">
        <span>{I18n.t('commons.offers.immediatePayment.contact.label')}</span>
        {user.email}
      </div>

      <p className="offers-form__payment-review__terms">
        <SanitizedRender
          html={I18n.t('commons.offers.immediatePayment.terms', {
            termsLinkOpen: `<a href='${TERMS_OF_USE_PATH}'>`,
            termsLinkClose: '</a>',
          })}
        />
      </p>
    </div>
  );
}

export const immediatePaymentReviewQuery = gql`query Commons_Offers_ImmediatePaymentReview(
  $input: Input_core_apimessages_EstimateNegotiationSalesTaxRequest,
) {
  estimateNegotiationSalesTax(input: $input) {
    amountTax {
      display
      amountCents
    }
  }
}`;

const connectImmediatePaymentReviewQuery = withGraphql<
  IExternalProps,
  CommonsOffersImmediatePaymentReview.Query,
  CommonsOffersImmediatePaymentReview.Variables
>(
  immediatePaymentReviewQuery,
  {
    options: props => ({
      ssr: false,
      variables: {
        input: {
          amountProduct: { amountCents: props.offerPrice.amountCents, currency: props.offerPrice.currency },
          amountShipping: { amountCents: props.shippingPrice.amountCents, currency: props.shippingPrice.currency },
          shippingAddressUuid: props.selectedAddress.id,
        },
      },
    }),
  },
);

export default connect<IExternalProps>([
  connectImmediatePaymentReviewQuery,
])(ImmediatePaymentReview);
